import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardHeader, CardTitle, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { 
  Calendar, Clock, ChevronRight, Shield, Phone, Car, Cloud, Zap, Printer, File, Mail
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const categoryIcons = {
  assurance: { icon: Shield, color: "text-blue-600", bg: "bg-blue-100" },
  telephonie: { icon: Phone, color: "text-purple-600", bg: "bg-purple-100" },
  leasing: { icon: Car, color: "text-red-600", bg: "bg-red-100" },
  saas: { icon: Cloud, color: "text-cyan-600", bg: "bg-cyan-100" },
  energie: { icon: Zap, color: "text-yellow-600", bg: "bg-yellow-100" },
  materiel: { icon: Printer, color: "text-gray-600", bg: "bg-gray-100" },
  autre: { icon: File, color: "text-slate-600", bg: "bg-slate-100" }
};

const formatDate = (dateStr) => {
  if (!dateStr) return "-";
  return new Date(dateStr).toLocaleDateString("fr-FR", { day: "2-digit", month: "short" });
};

const formatCurrency = (amount) => {
  if (!amount) return "-";
  return new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(amount);
};

export default function EngagementsCalendrier() {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ kpis: {}, echeances: [] });
  
  // Generate 12 months
  const months = [];
  const now = new Date();
  for (let i = 0; i < 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() + i, 1);
    months.push({
      key: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`,
      label: d.toLocaleDateString("fr-FR", { month: "short", year: "2-digit" }),
      month: d.getMonth(),
      year: d.getFullYear()
    });
  }

  useEffect(() => {
    fetchEcheances();
  }, []);

  const fetchEcheances = async () => {
    try {
      const res = await axios.get(`${API}/api/engagements/echeances`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(res.data);
    } catch (error) {
      console.error("Error:", error);
      toast.error("Erreur de chargement");
    } finally {
      setLoading(false);
    }
  };

  const getMonthForDate = (dateStr) => {
    if (!dateStr) return null;
    const d = new Date(dateStr);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  };

  if (loading) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-bold text-slate-900 mb-6">Calendrier des échéances</h1>
        <div className="animate-pulse space-y-4">
          {[1, 2, 3].map(i => <div key={i} className="h-20 bg-slate-100 rounded-lg" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="engagements-calendrier">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Calendrier des échéances</h1>
          <p className="text-slate-500">Vue sur 12 mois glissants</p>
        </div>
        <Button variant="outline">
          <Mail className="w-4 h-4 mr-2" />
          Envoyer récapitulatif
        </Button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-3 gap-4">
        <Card className="bg-gradient-to-br from-blue-50 to-white">
          <CardContent className="p-4">
            <p className="text-sm text-slate-500">Préavis ce mois</p>
            <p className="text-3xl font-bold text-blue-600">{data.kpis.preavis_ce_mois || 0}</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-orange-50 to-white">
          <CardContent className="p-4">
            <p className="text-sm text-slate-500">Dans les 30 jours</p>
            <p className="text-3xl font-bold text-orange-600">{data.kpis.preavis_30_jours || 0}</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-red-50 to-white">
          <CardContent className="p-4">
            <p className="text-sm text-slate-500">Dans les 60 jours</p>
            <p className="text-3xl font-bold text-red-600">{data.kpis.preavis_60_jours || 0}</p>
          </CardContent>
        </Card>
      </div>

      {/* Timeline */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="w-5 h-5" />
            Timeline 12 mois
          </CardTitle>
        </CardHeader>
        <CardContent>
          {/* Month headers */}
          <div className="flex border-b mb-4 pb-2">
            <div className="w-64 shrink-0" />
            {months.map(m => (
              <div key={m.key} className="flex-1 text-center text-xs font-medium text-slate-500 uppercase">
                {m.label}
              </div>
            ))}
          </div>

          {/* Contract rows */}
          {data.echeances.length === 0 ? (
            <p className="text-center text-slate-500 py-8">Aucune échéance à afficher</p>
          ) : (
            <div className="space-y-3">
              {data.echeances.map(contrat => {
                const cat = categoryIcons[contrat.categorie] || categoryIcons.autre;
                const Icon = cat.icon;
                const preavisMonth = getMonthForDate(contrat.date_limite_preavis);
                const echeanceMonth = getMonthForDate(contrat.date_echeance);
                
                return (
                  <div 
                    key={contrat.contrat_id} 
                    className="flex items-center hover:bg-slate-50 rounded-lg cursor-pointer transition-colors"
                    onClick={() => navigate(`/engagements/contrats/${contrat.contrat_id}`)}
                  >
                    {/* Contract info */}
                    <div className="w-64 shrink-0 flex items-center gap-3 pr-4">
                      <div className={`w-8 h-8 rounded ${cat.bg} flex items-center justify-center`}>
                        <Icon className={`w-4 h-4 ${cat.color}`} />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-slate-900 truncate">{contrat.prestataire}</p>
                        <p className="text-xs text-slate-500 truncate">{contrat.nom_contrat}</p>
                      </div>
                    </div>
                    
                    {/* Timeline bars */}
                    <div className="flex-1 flex h-10 items-center">
                      {months.map(m => {
                        const isPreavis = preavisMonth === m.key;
                        const isEcheance = echeanceMonth === m.key;
                        
                        return (
                          <div key={m.key} className="flex-1 flex items-center justify-center">
                            {isPreavis && (
                              <div className="relative group">
                                <div className="w-4 h-4 rounded-full bg-red-500 border-2 border-white shadow-md" />
                                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 hidden group-hover:block bg-slate-800 text-white text-xs px-2 py-1 rounded whitespace-nowrap">
                                  Préavis: {formatDate(contrat.date_limite_preavis)}
                                </div>
                              </div>
                            )}
                            {isEcheance && !isPreavis && (
                              <div className="relative group">
                                <div className="w-4 h-4 rounded-full bg-blue-400 border-2 border-white shadow-md" />
                                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 hidden group-hover:block bg-slate-800 text-white text-xs px-2 py-1 rounded whitespace-nowrap">
                                  Échéance: {formatDate(contrat.date_echeance)}
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                    
                    {/* Amount & days */}
                    <div className="w-32 shrink-0 text-right pr-2">
                      <p className="text-sm font-medium">{formatCurrency(contrat.montant_mensuel_ht)}/m</p>
                      {contrat.jours_restants != null && (
                        <Badge className={
                          contrat.jours_restants <= 7 ? "bg-red-100 text-red-700" :
                          contrat.jours_restants <= 30 ? "bg-orange-100 text-orange-700" :
                          "bg-slate-100 text-slate-600"
                        }>
                          <Clock className="w-3 h-3 mr-1" />
                          {contrat.jours_restants}j
                        </Badge>
                      )}
                    </div>
                    
                    <ChevronRight className="w-4 h-4 text-slate-400" />
                  </div>
                );
              })}
            </div>
          )}
          
          {/* Legend */}
          <div className="flex items-center gap-6 mt-6 pt-4 border-t text-sm text-slate-500">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-red-500" />
              Date limite préavis
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-blue-400" />
              Date d'échéance
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
